import type { HealthStatus } from "@cleanbudget/contract/health";
import { Badge } from "@cleanbudget/ui/components/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { orpc } from "@/lib/orpc";

/** `GET /api/health`, read through the query cache the route's loader filled. */
export const healthQuery = () => orpc.health.check.queryOptions();

const serverTime = new Intl.DateTimeFormat("nl-NL", {
	dateStyle: "medium",
	timeStyle: "medium",
	timeZone: "Europe/Amsterdam",
});

export function HealthCard() {
	const { t } = useTranslation("health");
	const { data } = useSuspenseQuery(healthQuery());

	return (
		<Card size="sm" className="max-w-sm" data-testid="health-card">
			<CardHeader>
				<CardTitle>{t("title")}</CardTitle>
			</CardHeader>
			<CardContent>
				<dl className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-2">
					<dt className="text-muted-foreground">{t("api")}</dt>
					<dd>
						<Badge variant={statusVariant(data.status)}>{t(`status.${data.status}`)}</Badge>
					</dd>
					<dt className="text-muted-foreground">{t("database")}</dt>
					<dd>
						<Badge variant={data.database === "ok" ? "secondary" : "destructive"}>
							{t(`databaseStatus.${data.database}`)}
						</Badge>
					</dd>
					<dt className="text-muted-foreground">{t("serverTime")}</dt>
					<dd>
						<time dateTime={data.time}>{serverTime.format(new Date(data.time))}</time>
					</dd>
				</dl>
			</CardContent>
		</Card>
	);
}

const statusVariant = (status: HealthStatus["status"]) =>
	match(status)
		.with("ok", () => "secondary" as const)
		.with("degraded", () => "destructive" as const)
		.exhaustive();
